<script setup lang="ts">
import { computed } from 'vue'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '../../../composables/useStyleData'
import { usePipe } from '../../../composables/usePipe'
import TipTap from '@render/lib/TipTap'
import type { RitchTextTPFields as Model, TextStylePreset } from './definition'

const fields = defineModel<Model>('fields', {
  required: true,
})

const pipe = usePipe()

const value = computed({
  get: () => {
    return pipe.getTranslation(fields.value.body?.key)
  },
  set: (newVal: object) => {
    // Used in editor only. Every FieldValues<> key is optional (a stored pipe may
    // omit any field), so guard rather than assuming `body` is set.
    const key = fields.value.body?.key
    if (key) pipe.setTranslation(key, newVal)
  },
})

const activePreset = computed<Exclude<TextStylePreset, 'custom'> | null>(() => {
  const s = fields.value.textStyle
  return s && s !== 'custom' ? s : null
})

const presetVar = (prop: 'family' | 'weight' | 'size' | 'line-height' | 'color'): string | null =>
  activePreset.value ? `var(--${activePreset.value}-${prop})` : null

const textFontFamily = computed(() => fields.value.fontFamily?.trim() || presetVar('family'))
const textFontWeight = computed(() => fields.value.fontWeight || presetVar('weight'))
const textColor = computed(() => getColorStyleData(fields.value.color) ?? presetVar('color'))
const textFontSize = computed(() => getSizeStyleData(fields.value.fontSize) ?? presetVar('size'))
const textLineHeight = computed(
  () => getSizeStyleData(fields.value.lineHeight) ?? presetVar('line-height'),
)
const textPadding = computed(() => getSpacingStyleData(fields.value.padding))
const textMargin = computed(() => getSpacingStyleData(fields.value.margin))
const paragraphMargin = computed(() => getSpacingStyleData(fields.value.paragraphMargin))
const hasParagraphMargin = computed(() => !!paragraphMargin.value)
</script>

<template>
  <div class="ritch-text-tp" :class="{ 'has-paragraph-margin': hasParagraphMargin }">
    <div v-if="!fields.body">No translation key set!</div>
    <TipTap v-else v-model="value" />
  </div>
</template>

<style scoped>
.ritch-text-tp {
  font-family: v-bind(textFontFamily);
  font-weight: v-bind(textFontWeight);
  color: v-bind(textColor);
  font-size: v-bind(textFontSize);
  line-height: v-bind(textLineHeight);
  padding: v-bind(textPadding);
  margin: v-bind(textMargin);
}

.ritch-text-tp.has-paragraph-margin :deep(p) {
  margin: v-bind(paragraphMargin);
}
</style>
